import { useMemo, useState } from "react";

import CategoryEditor from "./category-editor";

import styles from "./chart-editor.module.css";
import { getChildren } from "./utils";

const ChartTypeTitle = ({ config, onCancel }) => {
    return (
        <div className={styles.chartHeader}>
            <h2>{
            (() => {
                switch(config.type) {
                    case "bar":
                        return "Edit Bar Chart";
                    case "line":
                        return "Edit Line Chart";
                    case "doughnut":
                        return "Edit Pie Chart"
                    default:
                        return ""
                }
            })()
        }
            </h2>
            <button 
                className={styles.closeButton}
                onClick={onCancel}
                aria-label="Close editor"
            >
                <span className="material-icons">close</span>
            </button>
        </div>
    );
}

const ChartTitle = ({ config, onConfigChanged }) => {
    return (
        <div className={styles.picker}>
            <h4>Chart title</h4>
            <input
                type="text"
                value={config.title}
                onChange={e => onConfigChanged({
                    ...config,
                    title: e.target.value
                })}
            />
        </div>
    );
}

const XAxisFieldPicker = ({ config, onConfigChanged, categories }) => {
    return (
        <>
            <div className={styles.picker}>
                <h4>Field</h4>
                <label>
                    <input
                        type="radio"
                        checked={config.xaxisField === "timestamp"}
                        onChange={() => onConfigChanged({
                            ...config,
                            xaxisField: "timestamp"
                        })}
                    />
                    <span>Time & date</span>
                </label>
                <label>
                    <input
                        type="radio"
                        checked={config.xaxisField === "category"}
                        onChange={() => onConfigChanged({
                            ...config,
                            xaxisField: "category",
                        })}
                    />
                    <span>Category</span>
                </label>
            </div>
            {config.xaxisField === "timestamp" &&
                <TimestampBucketPicker
                    config={config}
                    onConfigChanged={onConfigChanged}
                />
            }
            {config.xaxisField === "category" &&
                <ChartCategoryPicker
                    config={config}
                    categoryFieldName="xaxisCategories"
                    categories={categories}
                    onConfigChanged={onConfigChanged}
                />
            }
        </>
    );
}

const YAxisFieldPicker = ({ config, onConfigChanged, categories }) => {
    return (
        <>
            <div className={styles.picker}>
                { config.type === "line" &&
                    <label>
                        <input
                            type="checkbox"
                            checked={config.cumulative}
                            onChange={e => onConfigChanged({
                                ...config,
                                cumulative: e.target.checked,
                            })}
                        />
                        <span>Show cumulative amount</span>
                    </label>
                }
                <h4>Field</h4>
                <label>
                    <input
                        type="radio"
                        checked={config.yaxisBreakdown === "none"}
                        onChange={() => onConfigChanged({
                            ...config,
                            yaxisBreakdown: "none"
                        })}
                    />
                    <span>None</span>
                </label>
                <label>
                    <input
                        type="radio"
                        checked={config.yaxisBreakdown === "category"}
                        onChange={() => onConfigChanged({
                            ...config,
                            yaxisBreakdown: "category"
                        })}
                    />
                    <span>Category</span>
                </label>
            </div>
            {config.yaxisBreakdown === "category" &&
                <ChartCategoryPicker
                    config={config}
                    categoryFieldName="yaxisCategories"
                    categories={categories}
                    onConfigChanged={onConfigChanged}
                />
            }
        </>
    );
};

const ElementFieldPicker = ({ config, onConfigChanged, categories }) => {
    return (
        <>
            <ChartCategoryPicker
                config={config}
                categoryFieldName="categories"
                categories={categories}
                onConfigChanged={onConfigChanged}
            />
        </>
    );
};

const TimestampBucketPicker = ({ config, onConfigChanged }) => {
    return (
        <div className={styles.picker}>
            <h4>How to group dates</h4>
            <label>
                <input
                    type="radio"
                    checked={config.timestampBuckets === "date"}
                    onChange={() => onConfigChanged({
                        ...config,
                        timestampBuckets: "date"
                    })}
                />
                <span>Date</span>
            </label>
            <label>
                <input
                    type="radio"
                    checked={config.timestampBuckets === "week"}
                    onChange={() => onConfigChanged({
                        ...config,
                        timestampBuckets: "week"
                    })}
                />
                <span>Week</span>
            </label>
            { /*
            <label>
                <input
                    type="radio"
                    checked={config.timestampBuckets === "month"}
                    onChange={() => onConfigChanged({
                        ...config,
                        timestampBuckets: "month"
                    })}
                />
                <span>Month</span>
            </label>
            */ }
        </div>
    );
};

const ChartCategoryPicker = ({ config, categoryFieldName, categories, title, onConfigChanged }) => {
    const checkedCategories = useMemo(() => {
        return Object.fromEntries(config[categoryFieldName].map(c => [ c, true ]), [ config ]);
    }, [ config ]);
    return (
        <div>
            <h4>{ title || "Categories to show" }</h4>
            <CategoryEditor
                categories={categories}
                checkedCategories={checkedCategories}
                onCheckedCategoriesChanged={newCheckedCategories => onConfigChanged({
                    ...config,
                    [ categoryFieldName ]: Object.entries(newCheckedCategories)
                        .filter(([ category, checked ]) => checked === true)
                        .map(([ category, checked ]) => category)
                })}
            />
        </div>
    );
}

export default ({ config, categories, onConfigChanged, onSave, onCancel, onDelete }) => {
    const topLevelCategories = useMemo(() => {
        if(!categories) {
            return [];
        }
        const categoriesByUri = Object.fromEntries(categories.map(c => [ c.uri, c ]));
        return getChildren(categoriesByUri, null).map(c => c.uri);
    }, [ categories ]);
    if(!config) {
        return (
            <div className={styles.root}>
            </div>
        );
    }
    const barOrLineChart = config.type === "bar" || config.type === "line";
    return (
        <div className={styles.root}>
            <ChartTypeTitle
                config={config}
                onCancel={onCancel}
            />
            <ChartTitle
                config={config}
                onConfigChanged={onConfigChanged}
            />
            { barOrLineChart &&
                <>
                    <h3>X Axis</h3>
                    <XAxisFieldPicker
                        config={config}
                        onConfigChanged={onConfigChanged}
                        categories={categories}
                    />
                </>
            }
            { barOrLineChart &&
                <>
                    <h3>Y Axis</h3>
                    <YAxisFieldPicker
                        config={config}
                        onConfigChanged={onConfigChanged}
                        categories={categories}
                    />
                </>
            }
            { config.type === "doughnut" &&
                <>
                    <h3>Y Axis</h3>
                    <ElementFieldPicker
                        config={config}
                        onConfigChanged={onConfigChanged}
                        categories={categories}
                    />
                </>
            }
            { (config.type === "bar" || config.type === "line" || config.type === "doughnut") &&
                <>
                    <h3>Filters</h3>
                    <div className={styles.picker}>
                        <label>
                            <input
                                type="checkbox"
                                checked={config.filterAllCategories}
                                onChange={e => onConfigChanged({
                                    ...config,
                                    filterAllCategories: e.target.checked,
                                    filterCategories: e.target.checked ? undefined : topLevelCategories
                                })}
                            />
                            <span>All categories</span>
                        </label>
                        { !config.filterAllCategories &&
                            <ChartCategoryPicker
                                config={config}
                                categoryFieldName="filterCategories"
                                categories={categories}
                                title="Categories to filter"
                                onConfigChanged={onConfigChanged}
                            />
                        }
                    </div>
                </>
            }
            <div className={styles.actions}>
                <button
                    onClick={onCancel}
                >Cancel</button>
                <button
                    onClick={onDelete}
                    className="danger"
                >Delete</button>
                <button
                    onClick={onSave}
                    className="primary"
                >Save</button>
            </div>
        </div>
    );
};
